import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enCommon from "./locales/en/common.json";
import mrCommon from "./locales/mr/common.json";
import hiCommon from "./locales/hi/common.json";
import enFir    from "./locales/en/fir.json";
import mrFir    from "./locales/mr/fir.json";
import hiFir    from "./locales/hi/fir.json";

/**
 * i18n configuration with namespaces:
 *   • common — app shell, nav, status, actions, errors, theme, lang, idle
 *   • fir    — FIR-domain strings
 *
 * Adding a new namespace:
 *   1. Drop `<locale>/<ns>.json` files into `src/locales/`
 *   2. Add them to `resources` below
 *   3. Use:   const { t } = useTranslation("<ns>");
 *
 * <html lang> is kept in sync with i18next.language so screen readers
 * pronounce Devanagari/Latin correctly.
 */

export const SUPPORTED_LANGS = ["mr", "en", "hi"] as const;
export type Lang = typeof SUPPORTED_LANGS[number];

const LS_KEY = "mhpol.lang";

function detectInitial(): Lang {
  try {
    const saved = localStorage.getItem(LS_KEY) as Lang | null;
    if (saved && (SUPPORTED_LANGS as readonly string[]).includes(saved)) return saved;
  } catch { /* ignore */ }
  const nav = (typeof navigator !== "undefined" ? navigator.language : "mr").toLowerCase();
  if (nav.startsWith("mr")) return "mr";
  if (nav.startsWith("hi")) return "hi";
  if (nav.startsWith("en")) return "en";
  return "mr";
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: enCommon, fir: enFir },
      mr: { common: mrCommon, fir: mrFir },
      hi: { common: hiCommon, fir: hiFir },
    },
    lng: detectInitial(),
    fallbackLng: "en",
    ns: ["common", "fir"],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    react: { useSuspense: false },
  });

function applyHtmlLang(lng: string) {
  const html = document.documentElement;
  // Map our short codes to BCP-47 with India region.
  const map: Record<string, string> = { mr: "mr-IN", hi: "hi-IN", en: "en-IN" };
  html.setAttribute("lang", map[lng] ?? lng);
  // Tag Devanagari class so legacy stylesheets that use [lang="mr"] keep working.
}
applyHtmlLang(i18n.language);
i18n.on("languageChanged", (lng) => {
  applyHtmlLang(lng);
  try { localStorage.setItem(LS_KEY, lng); } catch { /* noop */ }
});

export function setLanguage(lng: Lang) {
  void i18n.changeLanguage(lng);
}

export default i18n;
